import { type Theme, Variant } from "@/types/theme";
import { useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import React, { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ThemeContext, themes } from "./theme-context";

const themeAtom = atomWithStorage<Theme>("theme", "auto", undefined, {
  getOnInit: true,
});

const variantState = atomWithStorage<Variant>(
  "variant",
  Variant.Green,
  undefined,
  {
    getOnInit: true,
  },
);

export default function ThemeProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [themeToastId, setThemeToastId] = useState<string | number | null>(
    null,
  );
  const [hasInitialValuesBeenSet, setHasInitialValuesBeenSet] = useState(false);
  const [prefersDark, setPrefersDark] = useState(
    window.matchMedia("(prefers-color-scheme: dark)").matches,
  );
  const [theme, setTheme] = useAtom(themeAtom);
  const [variant, setVariant] = useAtom(variantState);
  const currentTheme = useMemo(() => {
    return theme === "auto" ? (prefersDark ? "dark" : "light") : theme;
  }, [theme, prefersDark]);

  // Listen for changes to the prefers-color-scheme media query
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersDark(e.matches);
    };

    // Modern browsers
    mediaQuery.addEventListener("change", handleChange);

    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  useLayoutEffect(() => {
    const actualTheme =
      theme === "auto" ? (prefersDark ? "dark" : "light") : theme;
    document.documentElement.dataset.theme = actualTheme;

    // Check if the current variant is compatible with the actual theme
    const isCompatibleVariant = themes[actualTheme].includes(variant);

    if (!isCompatibleVariant) {
      // Use the default variant for the current theme
      const newVariant =
        actualTheme === "dark" ? Variant.Green : Variant.DarkBlue;
      setVariant(newVariant);
      document.documentElement.dataset.variant = newVariant;
    } else {
      document.documentElement.dataset.variant = variant;
    }
    setHasInitialValuesBeenSet(true);
  }, [theme, variant, prefersDark, setVariant]);

  useEffect(() => {
    if (!hasInitialValuesBeenSet) return;
    setThemeToastId(
      toast.success("Tema opdateret og gemt", {
        id: themeToastId ?? undefined,
        onAutoClose: () => setThemeToastId(null),
        onDismiss: () => setThemeToastId(null),
      }),
    );
    // The missing dependencies here are intentional to avoid trigger on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, variant]);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        variant,
        setTheme,
        setVariant,
        currentTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
